<script setup lang="ts">
import { computed, type Component } from 'vue'
import { X } from '@lucide/vue'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/**
 * Modale de l'application (SP-37) : enveloppe du `Dialog` shadcn avec le dessin commun à
 * toutes les feuilles, web et mobile — un bandeau teinté en tête (dégradé tiré d'un jeton du
 * thème vers la surface), une pastille d'icône assortie, le titre en serif, et la pousse de
 * la marque en filigrane dans le coin. Une teinte dominante par modale, jamais un
 * arc-en-ciel ; `destructive` pour les confirmations de suppression.
 *
 * Règle MOA du 22 octobre 2026 : pas de sous-titre explicatif par défaut. Le slot
 * `description` n'existe que pour une information propre à l'action (un nom, une date).
 */
export type DialogTint = 'primary' | 'rose' | 'honey' | 'coral' | 'sage' | 'destructive'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    tint?: DialogTint
    icon?: Component
    size?: 'sm' | 'md' | 'lg' | 'xl'
    contentClass?: string
  }>(),
  { tint: 'primary', size: 'md', icon: undefined, contentClass: undefined },
)
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const SIZES: Record<NonNullable<typeof props.size>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
}

/* Dessin de la modale (demande MOA du 22 octobre 2026 : une vraie signature, mais sobre) :
   un voile léger de la teinte sur toute la surface, un halo flouté de la même teinte dans le
   coin supérieur droit (comme les orbes de fond de l'application), la pousse de la marque en
   grand filigrane en bas à droite, et une pastille d'icône pleine en dégradé. Une seule
   teinte par modale ; tout est dosé par `color-mix` sur le jeton pour suivre les deux thèmes. */
const TINT_VAR: Record<DialogTint, { color: string; foreground: string }> = {
  primary: { color: 'var(--primary)', foreground: 'var(--accent-foreground)' },
  rose: { color: 'var(--rose)', foreground: 'var(--rose-foreground)' },
  honey: { color: 'var(--honey)', foreground: 'var(--honey-foreground)' },
  coral: { color: 'var(--coral)', foreground: 'var(--coral-foreground)' },
  sage: { color: 'var(--sage)', foreground: 'var(--sage)' },
  destructive: { color: 'var(--destructive)', foreground: 'var(--destructive)' },
}
const surfaceStyle = computed(() => {
  const { color } = TINT_VAR[props.tint]
  return {
    background: `linear-gradient(160deg, color-mix(in srgb, ${color} 14%, transparent) 0%, color-mix(in srgb, ${color} 5%, transparent) 50%, transparent 100%), var(--popover)`,
  }
})
const haloStyle = computed(() => ({
  background: `radial-gradient(closest-side, color-mix(in srgb, ${TINT_VAR[props.tint].color} 38%, transparent), transparent)`,
}))
const chipStyle = computed(() => {
  const { color } = TINT_VAR[props.tint]
  return {
    background: `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 72%, var(--honey)))`,
    boxShadow: `0 8px 18px color-mix(in srgb, ${color} 35%, transparent)`,
  }
})
const motifStyle = computed(() => ({ color: TINT_VAR[props.tint].color }))
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent
      :show-close-button="false"
      :class="
        cn(
          'flex max-h-[calc(100vh-2rem)] flex-col gap-0 overflow-hidden rounded-3xl border-border bg-popover p-0 shadow-glass',
          SIZES[size],
          contentClass,
        )
      "
      :style="surfaceStyle"
    >
      <!-- Halo flouté de la teinte, comme les orbes de fond de l'application. -->
      <span
        class="pointer-events-none absolute -top-24 -right-20 size-72 rounded-full blur-2xl"
        :style="haloStyle"
        aria-hidden="true"
      />
      <!-- La pousse de la marque, en grand filigrane : même dessin que BrandMark.vue. -->
      <svg
        viewBox="0 0 48 48"
        class="pointer-events-none absolute -right-14 -bottom-16 size-72 opacity-[0.07] dark:opacity-[0.1]"
        :style="motifStyle"
        aria-hidden="true"
      >
        <path
          d="M24 41 C24 36 24 31 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.8"
          stroke-linecap="round"
        />
        <path d="M24 30 C16.5 30.5 11 25 10.5 16.5 C18.5 16.5 24 22.5 24 30 Z" fill="currentColor" />
        <path d="M24 24 C24 15.5 29.5 9.5 37.5 9 C37 17.5 31.5 23.5 24 24 Z" fill="currentColor" />
      </svg>

      <div class="relative shrink-0 px-7 pt-7 pb-4">
        <DialogHeader class="relative gap-1.5 pr-8 text-left">
          <div class="flex items-center gap-3">
            <span
              v-if="icon"
              class="grid size-12 shrink-0 place-items-center rounded-[15px] text-white"
              :style="chipStyle"
              aria-hidden="true"
            >
              <component :is="icon" class="size-6" :stroke-width="1.8" />
            </span>
            <DialogTitle class="font-serif text-[26px] leading-tight font-medium tracking-tight">{{
              title
            }}</DialogTitle>
          </div>
          <DialogDescription
            v-if="$slots.description"
            class="font-serif text-base text-honey-foreground italic"
          >
            <slot name="description" />
          </DialogDescription>
        </DialogHeader>

        <DialogClose
          class="absolute top-4 right-4 grid size-8 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
          aria-label="Fermer"
        >
          <X class="size-4" :stroke-width="1.9" />
        </DialogClose>
      </div>

      <div class="relative grid gap-4 overflow-y-auto px-7 pt-2 pb-7">
        <slot />
      </div>
    </DialogContent>
  </Dialog>
</template>
